import { z } from "zod";
import { Permission } from "./user";
import { State } from "./team";

export function fallback<T>(schema: z.ZodSchema<T>, value: T) {
  return z.any().transform((val) => {
    const safe = schema.safeParse(val);
    return safe.success ? safe.data : value;
  });
}

export const userProjectPermissionSchema = z.object({
  id: z.string(),
  state: fallback(z.nativeEnum(State), State.active),
  permission: fallback(z.nativeEnum(Permission), Permission.editor),
});

export type UserProjectPermission = z.infer<typeof userProjectPermissionSchema>;

function userProjectPermissionSchemaFromDBModel(obj: any): UserProjectPermission {
  return userProjectPermissionSchema.parse({
    id: obj.id.toString(),
    state: obj.state,
    permission: obj.permission,
  });
}

export const projectSchema = z.object({
  name: z.string(),
  documentId: z.string(),
  urlId: z.string(),
  updatedAt: z.date(),
  accountId: z.string(),
  teamDocumentId: z.string(),
  isOwner: z.boolean().default(false),
  permission: z.nativeEnum(Permission).default(Permission.editor),
  state: fallback(z.nativeEnum(State), State.active),
  userProjectPermissions: z.array(userProjectPermissionSchema),
  createdByUserId: z.number().optional(),
});

export type Project = z.infer<typeof projectSchema>;

export function projectFromDBModel(obj: any): Project {
  return projectSchema.parse({
    name: obj.name,
    documentId: obj.document_id,
    urlId: obj.id.toString(),
    updatedAt: new Date(obj.updated_at),
    accountId: obj.account_id.toString(),
    teamDocumentId: obj.team_document_id,
    isOwner: obj.is_owner,
    permission: obj.permission,
    state: obj.state,
    userProjectPermissions: obj.userProjectPermissions?.map(userProjectPermissionSchemaFromDBModel) || [],
    createdByUserId: obj.created_by_user_id,
  });
}

export const projectDBSchema = z.object({
  id: z.number(),
  name: z.string(),
  document_id: z.string(),
  updated_at: z.date(),
  account_id: z.string(),
  team_document_id: z.string(),
  is_owner: z.boolean().default(false),
  permission: z.nativeEnum(Permission).default(Permission.editor),
  state: fallback(z.nativeEnum(State), State.active),
  created_by_user_id: z.number().optional(),
});

export type ProjectDBSchema = z.infer<typeof projectDBSchema>;

export const projectsResponseSchema = z.object({
  projects: z.array(projectSchema),
});

export type ProjectsResponse = z.infer<typeof projectsResponseSchema>;

const projectCacheSchema = z.record(
  z.string(), // projectId key
  z.object({
    project: projectSchema,
    lastUpdated: z.date(),
  })
);

export type ProjectCache = z.infer<typeof projectCacheSchema>;
